/**
 * Importação GLOBAL: um arquivo, VÁRIAS entidades por linha, com os vínculos declarados.
 *
 * Nasceu de uma planilha de residenciais geriátricos: cada linha traz o paciente (nome, CPF,
 * nascimento) E a casa que o abriga (nome, CNPJ) — dois cadastros e uma aresta entre eles, sem
 * repetir dado nenhum. As três fases são as mesmas da importação por entidade:
 *  1. upload   — o arquivo + QUAIS entidades a linha traz (cada uma com um apelido: "paciente",
 *                "casa"); a IA mapeia cada coluna à entidade dona dela.
 *  2. revisão  — um bloco de mapeamento por entidade + a seção de VÍNCULOS ligando os blocos.
 *  3. progresso — polling do estado, igual à importação de sempre.
 */
import { ArrowLeft, ArrowRight, FileSpreadsheet, Plus, Trash2, Upload } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ComboReferencia } from '@/components/crud/ComboReferencia';
import { ProgressoImportacao } from '@/components/crud/ProgressoImportacao';
import { SeletorTags } from '@/components/crud/SeletorTags';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { Tabela, Tbody, Td, Th, Thead, Tr } from '@/components/ui/tabela';
import {
  type AnaliseGlobal,
  analisarPlanilhaGlobal,
  type BlocoEntidade,
  confirmarImportacaoGlobal,
  type EstadoImportacao,
  statusImportacao,
  type VinculoDeclarado,
} from '@/lib/api';

const IGNORAR = 'ignorar';

//: As entidades que uma linha pode trazer, com o apelido que a tela sugere para cada uma.
const TIPOS: { tipo: string; titulo: string; apelido: string }[] = [
  { tipo: 'pessoa', titulo: 'Pessoa', apelido: 'pessoa' },
  { tipo: 'empresa', titulo: 'Empresa / instituição', apelido: 'empresa' },
  { tipo: 'condominio', titulo: 'Condomínio', apelido: 'condominio' },
  { tipo: 'imovel', titulo: 'Imóvel', apelido: 'imovel' },
];

//: Rótulo humano das naturezas de vínculo (o valor cru é o que vai para o banco).
const ROTULO_NATUREZA: Record<string, string> = {
  societario: 'Sócio (societário)',
  trabalhista: 'Funcionário (trabalhista)',
  cliente: 'Cliente',
  paciente: 'Paciente',
  aluno: 'Aluno',
  morador: 'Morador',
  fornecedor: 'Fornecedor',
  outro: 'Outro',
  proprietario: 'Proprietário',
  administradora: 'Administradora',
  pertence: 'Pertence a',
  sindico: 'Síndico',
  subsindico: 'Subsíndico',
  conselheiro: 'Conselheiro',
  zelador: 'Zelador',
  contato_administradora: 'Contato da administradora',
};

const rotuloNatureza = (n: string) => ROTULO_NATUREZA[n] ?? n.replace(/_/g, ' ');

//: Vínculo com cargo só onde o cargo significa algo (o paciente de um residencial não tem cargo).
const usaCargo = (natureza: string) => natureza === 'societario' || natureza === 'trabalhista';

const ehContato = (chaveComPrefixo: string | undefined) => {
  const chave = (chaveComPrefixo ?? '').split('.').slice(1).join('.');
  return chave === 'telefone' || chave === 'email';
};

/** Uma prévia do conteúdo de uma coluna (primeiros valores não-vazios da amostra). */
function previa(amostra: Record<string, string>[], coluna: string): string {
  const vals = amostra.map((l) => l[coluna]).filter((v) => v?.trim());
  return vals.slice(0, 3).join(' · ') || '—';
}

export function ImportarGlobal() {
  const navegar = useNavigate();

  const [fase, setFase] = useState<'upload' | 'revisao' | 'progresso'>('upload');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [prompt, setPrompt] = useState('');
  const [analisando, setAnalisando] = useState(false);
  // Entidades marcadas no upload: tipo → apelido (o nome pelo qual os vínculos a chamam).
  const [marcados, setMarcados] = useState<Record<string, string>>({});

  const [analise, setAnalise] = useState<AnaliseGlobal | null>(null);
  const [atribuicoes, setAtribuicoes] = useState<Record<string, string>>({});
  const [constantes, setConstantes] = useState<Record<string, Record<string, string>>>({});
  const [referencias, setReferencias] = useState<Record<string, string>>({});
  const [separadores, setSeparadores] = useState<Record<string, string>>({});
  const [tags, setTags] = useState<Record<string, string[]>>({});
  const [reaproveitar, setReaproveitar] = useState<Record<string, boolean>>({});
  const [vinculos, setVinculos] = useState<VinculoDeclarado[]>([]);
  const [salvarMolde, setSalvarMolde] = useState(false);
  const [nomeMolde, setNomeMolde] = useState('');
  const [confirmando, setConfirmando] = useState(false);

  const [estado, setEstado] = useState<EstadoImportacao | null>(null);
  const importacaoIdRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const blocosMarcados: BlocoEntidade[] = useMemo(
    () =>
      TIPOS.filter((t) => marcados[t.tipo] !== undefined).map((t) => ({
        tipo: t.tipo,
        apelido: (marcados[t.tipo] || t.apelido).trim(),
      })),
    [marcados],
  );

  const alternarTipo = (tipo: string, apelidoPadrao: string) =>
    setMarcados((m) => {
      const novo = { ...m };
      if (tipo in novo) delete novo[tipo];
      else novo[tipo] = apelidoPadrao;
      return novo;
    });

  const analisar = useCallback(async () => {
    if (!arquivo) return;
    if (blocosMarcados.length === 0) {
      toast.error('Marque ao menos uma entidade presente no arquivo.');
      return;
    }
    const apelidos = blocosMarcados.map((b) => b.apelido);
    if (new Set(apelidos).size !== apelidos.length || apelidos.some((a) => !a)) {
      toast.error('Cada entidade precisa de um apelido próprio (e não vazio).');
      return;
    }
    setAnalisando(true);
    const r = await analisarPlanilhaGlobal(arquivo, prompt, blocosMarcados);
    setAnalisando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    setAnalise(r.dados);
    setAtribuicoes(r.dados.atribuicoes);
    setConstantes(r.dados.constantes_por_bloco ?? {});
    setReferencias(r.dados.referencia_por_bloco ?? {});
    setSeparadores(r.dados.separadores ?? {});
    setVinculos(r.dados.vinculos ?? []);
    setReaproveitar(
      Object.fromEntries(
        r.dados.blocos.filter((b) => b.tipo === 'pessoa').map((b) => [b.apelido, true]),
      ),
    );
    importacaoIdRef.current = r.dados.importacao_id;
    setFase('revisao');
    const msg =
      r.dados.via === 'molde'
        ? 'Layout reconhecido — mapa e vínculos preenchidos por um molde salvo.'
        : r.dados.via === 'manual'
          ? `${r.dados.total_linhas} linhas lidas. IA indisponível — mapeie as colunas à mão.`
          : `${r.dados.total_linhas} linhas lidas. Confira o mapa sugerido pela IA.`;
    if (r.dados.via === 'manual') toast.info(msg);
    else toast.success(msg);
  }, [arquivo, prompt, blocosMarcados]);

  const confirmar = useCallback(async () => {
    const id = importacaoIdRef.current;
    if (!id || !analise) return;
    if (salvarMolde && !nomeMolde.trim()) {
      toast.error('Dê um nome ao molde ou desmarque a opção.');
      return;
    }
    setConfirmando(true);
    const fixos = Object.fromEntries(
      Object.entries(constantes).map(([apelido, mapa]) => [
        apelido,
        Object.fromEntries(Object.entries(mapa).filter(([, v]) => v.trim())),
      ]),
    );
    const seps = Object.fromEntries(
      Object.entries(separadores).filter(([col, sep]) => sep.trim() && ehContato(atribuicoes[col])),
    );
    const r = await confirmarImportacaoGlobal(id, {
      atribuicoes,
      blocos: analise.blocos.map((b) => ({ apelido: b.apelido, tipo: b.tipo })),
      vinculos,
      constantesPorBloco: fixos,
      referenciaPorBloco: Object.fromEntries(
        Object.entries(referencias).filter(([, v]) => v?.trim()),
      ),
      separadores: seps,
      tagsPorBloco: tags,
      reaproveitarPorNome: reaproveitar,
      molde: salvarMolde ? { nome: nomeMolde.trim() } : undefined,
    });
    setConfirmando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    setFase('progresso');
  }, [
    analise,
    atribuicoes,
    constantes,
    referencias,
    separadores,
    tags,
    reaproveitar,
    vinculos,
    salvarMolde,
    nomeMolde,
  ]);

  // Polling do progresso enquanto processa. Para ao concluir/errar; limpa no desmonte.
  useEffect(() => {
    if (fase !== 'progresso') return;
    const id = importacaoIdRef.current;
    if (!id) return;
    let vivo = true;
    let timer: ReturnType<typeof setTimeout>;
    const bater = async () => {
      const r = await statusImportacao(id);
      if (!vivo) return;
      if (r.ok) {
        setEstado(r.dados);
        if (r.dados.estado === 'concluida' || r.dados.estado === 'erro') return;
      }
      timer = setTimeout(bater, 1200);
    };
    void bater();
    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, [fase]);

  // Pares de blocos que podem ser ligados (o back diz quais e com que naturezas).
  const pares = useMemo(() => {
    if (!analise) return [];
    return Object.entries(analise.naturezas_por_par).map(([chave, naturezas]) => {
      const [de, para] = chave.split('|');
      return { de, para, naturezas };
    });
  }, [analise]);

  const naturezasDo = (de: string, para: string) =>
    pares.find((p) => p.de === de && p.para === para)?.naturezas ?? [];

  const adicionarVinculo = () => {
    const primeiro = pares[0];
    if (!primeiro) {
      toast.error('Não há par de entidades ligável neste arquivo.');
      return;
    }
    setVinculos((v) => [
      ...v,
      { de: primeiro.de, para: primeiro.para, natureza: primeiro.naturezas[0], rotulo: '' },
    ]);
  };

  const mudarVinculo = (i: number, campo: keyof VinculoDeclarado, valor: string) =>
    setVinculos((lista) =>
      lista.map((v, idx) => {
        if (idx !== i) return v;
        const novo = { ...v, [campo]: valor };
        // Trocar de/para pode invalidar a natureza escolhida: cai na primeira válida do par.
        if (campo === 'de' || campo === 'para') {
          const validas = naturezasDo(novo.de, novo.para);
          if (!validas.includes(novo.natureza)) novo.natureza = validas[0] ?? 'outro';
        }
        return novo;
      }),
    );

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-start gap-3">
        <Button
          variant="fantasma"
          size="icon"
          onClick={() => navegar('/cadastro/pessoas')}
          aria-label="Voltar"
        >
          <ArrowLeft />
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="size-5 text-accent" />
            <h1 className="tipo-titulo">Importar planilha · várias entidades</h1>
          </div>
          <p className="mt-1 tipo-corpo-grande text-text-secondary">
            Uma linha, vários cadastros. Diga o que o arquivo traz (ex.: o paciente e o residencial
            que o abriga) e como eles se ligam — o sistema atualiza quem já existe e cria só o
            vínculo que falta.
          </p>
        </div>
      </header>

      {fase === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>1. Arquivo, entidades e contexto</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border-strong bg-input py-10 text-center transition-colors hover:bg-surface-hover"
            >
              <Upload className="size-6 text-text-tertiary" />
              <span className="tipo-corpo text-text">
                {arquivo ? arquivo.name : 'Clique para escolher um Excel (.xlsx) ou CSV'}
              </span>
              {arquivo && (
                <span className="tipo-rotulo text-text-tertiary">
                  {(arquivo.size / 1024).toFixed(0)} KB
                </span>
              )}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xlsm,.xls,.csv,.txt,.tsv"
              className="hidden"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />

            <div className="flex flex-col gap-3 rounded-md border border-border bg-input/40 p-4">
              <div>
                <p className="tipo-rotulo text-text-secondary">O que CADA LINHA do arquivo traz</p>
                <p className="tipo-rotulo text-text-tertiary">
                  Marque as entidades presentes. O apelido é o nome pelo qual você vai chamá-las na
                  hora de declarar os vínculos — troque para o que a planilha significa ("paciente",
                  "casa", "responsável").
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {TIPOS.map((t) => {
                  const marcado = t.tipo in marcados;
                  return (
                    <div key={t.tipo} className="flex items-center gap-2">
                      <label className="flex min-w-44 items-center gap-2 tipo-corpo text-text">
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={() => alternarTipo(t.tipo, t.apelido)}
                          className="size-4 accent-primary"
                        />
                        {t.titulo}
                      </label>
                      {marcado && (
                        <Input
                          value={marcados[t.tipo]}
                          onChange={(e) =>
                            setMarcados((m) => ({ ...m, [t.tipo]: e.target.value }))
                          }
                          placeholder="apelido"
                          className="h-9 max-w-40"
                          aria-label={`Apelido de ${t.titulo}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="prompt-global" className="tipo-rotulo text-text-secondary">
                Descreva a planilha (opcional) — ajuda a IA a saber de quem é cada coluna
              </label>
              <textarea
                id="prompt-global"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                placeholder="Ex.: cada linha é um idoso e o residencial onde ele mora; o CNPJ é da casa."
                className="w-full rounded-md border border-border bg-input px-3 py-2 text-[15px] text-text placeholder:text-text-tertiary focus-visible:border-border-strong focus-visible:shadow-(--focus-ring) focus-visible:outline-none"
              />
            </div>
            <div className="flex justify-end">
              <Button variant="primario" onClick={analisar} disabled={!arquivo || analisando}>
                {analisando ? <Spinner /> : <FileSpreadsheet />}
                {analisando ? 'Analisando…' : 'Analisar planilha'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {fase === 'revisao' && analise && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>2. Revise o mapa das colunas</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p className="tipo-corpo text-text-secondary">
                {analise.total_linhas} linhas. Cada coluna pertence a UMA das entidades — o select
                mostra o apelido dela antes do campo.
              </p>
              <div className="overflow-x-auto">
                <Tabela>
                  <Thead>
                    <Th>Coluna da planilha</Th>
                    <Th>Amostra</Th>
                    <Th>Corresponde a</Th>
                  </Thead>
                  <Tbody>
                    {analise.colunas.map((col) => (
                      <Tr key={col}>
                        <Td mono>{col}</Td>
                        <Td>
                          <span className="tipo-rotulo text-text-tertiary">
                            {previa(analise.amostra, col)}
                          </span>
                        </Td>
                        <Td>
                          <select
                            value={atribuicoes[col] ?? IGNORAR}
                            title={analise.chaves[atribuicoes[col]] ?? undefined}
                            onChange={(e) =>
                              setAtribuicoes((a) => ({ ...a, [col]: e.target.value }))
                            }
                            className="h-10 w-full min-w-64 rounded-md border border-border bg-input px-3 text-[15px] text-text focus-visible:border-border-strong focus-visible:outline-none"
                          >
                            <option value={IGNORAR}>— Ignorar —</option>
                            {analise.blocos.map((b) => (
                              <optgroup key={b.apelido} label={`${b.titulo} · ${b.apelido}`}>
                                {Object.entries(b.rotulos).map(([chave, rotulo]) => (
                                  <option key={chave} value={`${b.apelido}.${chave}`}>
                                    {rotulo}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          {ehContato(atribuicoes[col]) && (
                            <label className="mt-1.5 flex items-center gap-1.5">
                              <span className="tipo-rotulo whitespace-nowrap text-text-tertiary">
                                vários por célula? separador
                              </span>
                              <input
                                value={separadores[col] ?? ''}
                                onChange={(e) =>
                                  setSeparadores((s) => ({ ...s, [col]: e.target.value }))
                                }
                                placeholder="ex.: |"
                                maxLength={4}
                                className="h-8 w-16 rounded-md border border-border bg-input px-2 text-center text-[15px] text-text focus-visible:border-border-strong focus-visible:outline-none"
                              />
                            </label>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Tabela>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Vínculos entre as entidades</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p className="tipo-corpo text-text-secondary">
                Diga como as entidades da MESMA linha se ligam. O rótulo é livre e vira o nome da
                aresta no grafo — reimportar o arquivo não duplica o vínculo.
              </p>
              {vinculos.length === 0 && (
                <p className="tipo-rotulo text-text-tertiary">
                  Nenhum vínculo declarado — os cadastros entram sem se ligar.
                </p>
              )}
              <div className="flex flex-col gap-3">
                {vinculos.map((v, i) => {
                  const validas = naturezasDo(v.de, v.para);
                  return (
                    <div
                      key={`${v.de}-${v.para}-${i}`}
                      className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-input/40 p-3"
                    >
                      <div className="flex flex-col gap-1">
                        <span className="tipo-rotulo text-text-tertiary">De</span>
                        <select
                          value={v.de}
                          onChange={(e) => mudarVinculo(i, 'de', e.target.value)}
                          className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text focus-visible:border-border-strong focus-visible:outline-none"
                        >
                          {analise.blocos.map((b) => (
                            <option key={b.apelido} value={b.apelido}>
                              {b.apelido}
                            </option>
                          ))}
                        </select>
                      </div>
                      <ArrowRight className="mb-2.5 size-4 text-text-tertiary" />
                      <div className="flex flex-col gap-1">
                        <span className="tipo-rotulo text-text-tertiary">Para</span>
                        <select
                          value={v.para}
                          onChange={(e) => mudarVinculo(i, 'para', e.target.value)}
                          className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text focus-visible:border-border-strong focus-visible:outline-none"
                        >
                          {analise.blocos.map((b) => (
                            <option key={b.apelido} value={b.apelido}>
                              {b.apelido}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="tipo-rotulo text-text-tertiary">Natureza do vínculo</span>
                        <select
                          value={v.natureza}
                          onChange={(e) => mudarVinculo(i, 'natureza', e.target.value)}
                          className="h-10 min-w-52 rounded-md border border-border bg-input px-3 text-[15px] text-text focus-visible:border-border-strong focus-visible:outline-none"
                        >
                          {validas.length === 0 && <option value="">— par não ligável —</option>}
                          {validas.map((n) => (
                            <option key={n} value={n}>
                              {rotuloNatureza(n)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="tipo-rotulo text-text-tertiary">
                          Rótulo no grafo (opcional)
                        </span>
                        <Input
                          value={v.rotulo ?? ''}
                          onChange={(e) => mudarVinculo(i, 'rotulo', e.target.value)}
                          placeholder="ex.: mora em"
                          className="max-w-52"
                        />
                      </div>
                      {usaCargo(v.natureza) && (
                        <div className="flex flex-col gap-1">
                          <span className="tipo-rotulo text-text-tertiary">Cargo (opcional)</span>
                          <Input
                            value={v.cargo ?? ''}
                            onChange={(e) => mudarVinculo(i, 'cargo', e.target.value)}
                            placeholder="ex.: Diretor"
                            className="max-w-44"
                          />
                        </div>
                      )}
                      <Button
                        variant="fantasma"
                        size="icon"
                        aria-label="Remover vínculo"
                        onClick={() => setVinculos((lista) => lista.filter((_, x) => x !== i))}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div>
                <Button variant="secundario" onClick={adicionarVinculo}>
                  <Plus /> Adicionar vínculo
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Ajustes por entidade</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              {analise.blocos.map((b) => (
                <div
                  key={b.apelido}
                  className="flex flex-col gap-3 rounded-md border border-border bg-input/40 p-4"
                >
                  <div>
                    <p className="tipo-rotulo text-text-secondary">
                      {b.titulo} · {b.apelido}
                    </p>
                    <p className="tipo-rotulo text-text-tertiary">
                      Registros com {b.chave_dedup} já cadastrado são{' '}
                      <span className="text-text">atualizados</span> (campos vazios preenchidos,
                      contatos acumulados) — nunca duplicados.
                    </p>
                  </div>

                  {b.tipo === 'pessoa' && (
                    <label className="flex items-start gap-2 tipo-corpo text-text">
                      <input
                        type="checkbox"
                        checked={reaproveitar[b.apelido] ?? true}
                        onChange={(e) =>
                          setReaproveitar((r) => ({ ...r, [b.apelido]: e.target.checked }))
                        }
                        className="mt-1 size-4 accent-primary"
                      />
                      <span>
                        Sem CPF, reaproveitar o cadastro de mesmo nome
                        <span className="block tipo-rotulo text-text-tertiary">
                          Só quando nada conflita (CPF, nascimento, nome da mãe, RG, título, PIS,
                          CNH ou sexo divergente refuta o par e cria um cadastro novo).
                        </span>
                      </span>
                    </label>
                  )}

                  <div>
                    <p className="tipo-rotulo text-text-secondary">Tags do lote (opcional)</p>
                    <SeletorTags
                      valor={tags[b.apelido] ?? []}
                      onChange={(v) => setTags((t) => ({ ...t, [b.apelido]: v }))}
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {b.referencia.endpoint && (
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor={`ref-${b.apelido}`}
                          className="tipo-rotulo text-text-secondary"
                        >
                          {b.referencia.rotulo} (para todos)
                        </label>
                        <ComboReferencia
                          id={`ref-${b.apelido}`}
                          valor={referencias[b.apelido] ?? ''}
                          onChange={(v) => setReferencias((r) => ({ ...r, [b.apelido]: v }))}
                          endpoint={b.referencia.endpoint}
                          permitirCriar
                        />
                      </div>
                    )}
                    {Object.entries(b.chaves_constante).map(([chave, desc]) => {
                      const opcoes = b.opcoes_constante?.[chave];
                      const valor = constantes[b.apelido]?.[chave] ?? '';
                      const mudar = (v: string) =>
                        setConstantes((c) => ({
                          ...c,
                          [b.apelido]: { ...(c[b.apelido] ?? {}), [chave]: v },
                        }));
                      return (
                        <div key={chave} className="flex flex-col gap-1">
                          <label
                            htmlFor={`fixo-${b.apelido}-${chave}`}
                            className="tipo-rotulo text-text-secondary"
                          >
                            {desc}
                          </label>
                          {opcoes ? (
                            <select
                              id={`fixo-${b.apelido}-${chave}`}
                              value={valor}
                              onChange={(e) => mudar(e.target.value)}
                              className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
                            >
                              <option value="">— não aplicar —</option>
                              {opcoes.map((o) => (
                                <option key={o} value={o}>
                                  {o.charAt(0).toUpperCase() + o.slice(1)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <Input
                              id={`fixo-${b.apelido}-${chave}`}
                              value={valor}
                              onChange={(e) => mudar(e.target.value)}
                              placeholder="deixe vazio para não aplicar"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <label className="flex items-center gap-2 tipo-corpo text-text">
                <input
                  type="checkbox"
                  checked={salvarMolde}
                  onChange={(e) => setSalvarMolde(e.target.checked)}
                  className="size-4 accent-primary"
                />
                Salvar este mapa (com os vínculos) como molde para planilhas de mesmo layout
              </label>
              {salvarMolde && (
                <Input
                  value={nomeMolde}
                  onChange={(e) => setNomeMolde(e.target.value)}
                  placeholder="Nome do molde — ex.: Residenciais geriátricos (layout do parceiro)"
                  className="max-w-md"
                />
              )}

              <div className="flex justify-between">
                <Button variant="secundario" onClick={() => setFase('upload')}>
                  Voltar
                </Button>
                <Button variant="primario" onClick={confirmar} disabled={confirmando}>
                  {confirmando ? <Spinner /> : <Upload />}
                  Confirmar e importar
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {fase === 'progresso' && (
        <ProgressoImportacao
          titulo="5. Importando"
          estado={estado}
          acoes={
            <>
              <Button
                variant="secundario"
                onClick={() => {
                  setFase('upload');
                  setArquivo(null);
                  setPrompt('');
                  setAnalise(null);
                  setEstado(null);
                  setVinculos([]);
                  importacaoIdRef.current = null;
                }}
              >
                Importar outra
              </Button>
              <Button variant="primario" onClick={() => navegar('/cadastro/pessoas')}>
                Ver cadastros
              </Button>
            </>
          }
        />
      )}
    </div>
  );
}
